import { useEffect } from 'react'
import { type Assessment } from 'shared/types'

import { type Entity, type OptIns, type Subject } from '~/domains/platform/infra/deus-ex-machina/ports'

import { organizationAccountRequestStatus } from './hooks/organization-account-request-status'
import { useKycStatusOnboarding } from './hooks/use-kyc-status-onboarding'
import { type KycEvents, eventsKycOnboarding } from './analitica'
import { type KYC, KycOnboarding } from './kyc-onboarding.component'

const oneStoneKycStatusMap: Record<string, KYC> = {
  pending_kyc: 'responsePending',
  pending_email_confirmation: 'responsePending',
  pending_analysis: 'analysis',
  approved: 'approved',
  rejected: 'rejected'
}

type KycStatusHandlerType = {
  subjectId: string
  kycStatus: KYC
  reason?: Assessment['reason']
  actionButton?: () => void
  children: React.ReactNode | React.ReactNode[]
  entity?: Entity<'banking' | 'acquirer'>
  subject?: Pick<Subject<OptIns>, 'kyc' | 'kycRequest'> & Partial<Pick<Subject<OptIns>, 'unsafe'>>
}

type IsKycPendingParams = Pick<KycStatusHandlerType, 'subject' | 'entity' | 'kycStatus'>

export function isKycPending({ subject, entity, kycStatus }: IsKycPendingParams) {
  const entityKyc = entity?.stoneAccount?.kyc
  const subjectKyc = subject?.kyc
  const isOneStoneKycPending = Boolean(subjectKyc?.shouldRedirect || entityKyc?.shouldRedirect)

  if (!isOneStoneKycPending) {
    return organizationAccountRequestStatus(entity, subject) ?? kycStatus
  }

  const rawStatus = subjectKyc?.shouldRedirect ? subjectKyc?.status : entityKyc?.status
  if (!rawStatus) return kycStatus

  return oneStoneKycStatusMap[rawStatus] ?? kycStatus
}

export const KycStatusHandler = ({
  subjectId,
  kycStatus,
  reason,
  actionButton,
  children,
  entity,
  subject
}: KycStatusHandlerType) => {
  const mappedKycStatus = isKycPending({ subject, entity, kycStatus })
  const { kycStatusPending } = useKycStatusOnboarding(subjectId, kycStatus, reason, entity, subject)

  const eventsKyc = (status: KYC): KycEvents | null => {
    const statusEventsMap: Record<KYC, KycEvents | null> = {
      responsePending: 'viewKycOnboardingScreen',
      analysis: 'viewKycAnalysisPendingScreen',
      rejected: 'viewKycRejectScreen',
      approved: null
    }
    return statusEventsMap[status]
  }

  useEffect(() => {
    const kycEvent = eventsKyc(mappedKycStatus)
    if (kycStatusPending && kycEvent) {
      eventsKycOnboarding(kycEvent)
    }
  }, [mappedKycStatus, kycStatusPending])

  if (kycStatusPending) {
    return (
      <div className="flex items-center justify-center" style={{ height: '80dvh' }}>
        <KycOnboarding kycStatus={mappedKycStatus} actionButton={actionButton} />
      </div>
    )
  }
  return children
}
